// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Summary rules editor: a Header field row (blueprint-sets Req 6.1).
import ColumnLayout from '@cloudscape-design/components/column-layout';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import { type HeaderFieldRule } from '@bda/shared';
import {
  type Ctx,
  ErrorText,
  isMissingRef,
  memberOptions,
  MISSING_REFERENCE_LABEL,
  pathOption,
  rowLabel,
  RuleRow,
  selectedMember,
  selectedPath,
  sourceError,
} from './summaryRulesShared';

// ---------- Header fields ----------

export function HeaderFieldRow({
  rule,
  index,
  ctx,
  onChange,
  onRemove,
}: {
  rule: HeaderFieldRule;
  index: number;
  ctx: Ctx;
  onChange: (r: HeaderFieldRule) => void;
  onRemove: () => void;
}) {
  const row = `Header field ${index + 1}`;
  const key = `headerFields.${index}`;
  const options = (ctx.paths.get(rule.source.memberId) ?? []).filter((p) => !p.table).map(pathOption);
  const srcError = sourceError(ctx.errors[`${key}.source`], rule.source.path);
  return (
    <RuleRow
      title={rule.label.trim() || row}
      marker={isMissingRef(ctx.errors[`${key}.source`], rule.source.path) ? MISSING_REFERENCE_LABEL : undefined}
      onRemove={onRemove}
      readOnly={ctx.readOnly}
    >
      <ColumnLayout columns={3}>
        <FormField label={rowLabel(row, 'Label')} errorText={ctx.errors[`${key}.label`]}>
          <Input
            value={rule.label}
            readOnly={ctx.readOnly}
            ariaLabel={`${row} label`}
            onChange={(e) => onChange({ ...rule, label: e.detail.value })}
          />
        </FormField>
        <FormField label={rowLabel(row, 'Blueprint')}>
          <Select
            selectedOption={selectedMember(ctx, rule.source.memberId)}
            options={memberOptions(ctx)}
            disabled={ctx.readOnly}
            ariaLabel={`${row} blueprint`}
            onChange={(e) => {
              const memberId = e.detail.selectedOption.value!;
              const first = (ctx.paths.get(memberId) ?? []).find((p) => !p.table);
              onChange({ ...rule, source: { memberId, path: first?.path ?? '' } });
            }}
          />
        </FormField>
        <FormField label={rowLabel(row, 'Field')} errorText={srcError}>
          <Select
            selectedOption={selectedPath(ctx, rule.source.memberId, rule.source.path)}
            options={options}
            placeholder="Choose a field"
            empty="This blueprint has no fields to show."
            disabled={ctx.readOnly}
            ariaLabel={`${row} field`}
            onChange={(e) => onChange({ ...rule, source: { ...rule.source, path: e.detail.selectedOption.value! } })}
          />
        </FormField>
      </ColumnLayout>
      <ErrorText>{ctx.errors[`${key}.id`]}</ErrorText>
    </RuleRow>
  );
}
